import { useMediaQuery } from '@chakra-ui/react';
import {
  ArrowLittleRightIcon,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  SimpleGrid,
  Skeleton,
} from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useAnalytics } from '@r-client/shared/data/analytics';

import { cardDataFromSeedrsRecord } from '../../utils/helper/europe-offering-card';
import { EuropeOfferingCard } from '../europe-offering-card/europe-offering-card';
import { useEuropeOfferingQuery } from './eu-offerings-query';

export interface IEuOfferingsProps {
  first: number;
  sort: 'most_recent_desc' | 'trending_desc';
  viewAllButtonHref?: string;
  viewAllButtonText?: string;
  heading?: string;
  headingColor?: string;
}

export const EuOfferings = observer(function EuOfferings({
  first,
  sort,
  viewAllButtonHref,
  viewAllButtonText,
  heading,
  headingColor,
}: IEuOfferingsProps) {
  const offeringsQuery = useEuropeOfferingQuery({
    page_size: first,
    sort,
  });

  const analytics = useAnalytics();

  const [isTablet] = useMediaQuery(
    `(min-width: 600px) and (max-width: 1170px)`
  );

  const showViewMoreButton = !!viewAllButtonHref;

  return (
    <Box data-testid="eu-offerings">
      {(heading || showViewMoreButton) && (
        <Flex
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          mb={{ base: '6', sm: '12' }}
          gap="8"
        >
          <Box>
            {heading && (
              <Heading as="h2" m="0" size="title-md" color={headingColor}>
                {heading}
              </Heading>
            )}
          </Box>

          {showViewMoreButton && (
            <Link
              href={viewAllButtonHref}
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
              onClick={() =>
                analytics.track({
                  name: 'click_view_all',
                })
              }
              variant="republic-default"
              size="republic-default"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={0}
            >
              <Button variant="secondary" textStyle="lg">
                {viewAllButtonText ?? 'Show all'} <ArrowLittleRightIcon />
              </Button>
            </Link>
          )}
        </Flex>
      )}
      {offeringsQuery?.state === 'pending' ? (
        <Grid
          mt="8"
          gap="9"
          gridTemplateColumns={{
            base: 'repeat(1, 1fr)',
            xs: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          }}
        >
          {Array.from({ length: first }, (_, i) => {
            return (
              <Skeleton
                isLoaded
                data-testid="skeleton-offering-card"
                key={`${i}_skeleton`}
                height={isTablet ? '308px' : '500px'}
                bgColor="grey.600"
                borderRadius="12px"
                opacity="8%"
              />
            );
          })}
        </Grid>
      ) : (
        <SimpleGrid
          columns={{ base: 1, sm: 3, md: 3 }}
          spacing={{ base: '6', lg: '35px' }}
          mt="6"
          mx="auto"
        >
          {offeringsQuery?.value?.result
            ?.slice(0, first)
            ?.map((offering) => cardDataFromSeedrsRecord(offering))
            .map((offering) => {
              return (
                <GridItem key={offering.slug} p="0">
                  <EuropeOfferingCard cardData={offering} />
                </GridItem>
              );
            })}
        </SimpleGrid>
      )}

      {showViewMoreButton && (
        <Link
          href={viewAllButtonHref}
          textDecoration="none"
          _hover={{ textDecoration: 'none' }}
          onClick={() =>
            analytics.track({
              name: 'click_view_all',
            })
          }
          variant="republic-default"
          size="republic-default"
          display={{ base: 'block', lg: 'none' }}
          mt="6"
        >
          <Button px="10" w="full" variant="secondary">
            {viewAllButtonText ?? 'Show all'} <ArrowLittleRightIcon />
          </Button>
        </Link>
      )}
    </Box>
  );
});
